// Formulaire de tickets — /ticket-form-settings. The form embedded on the
// tenant's website: customers open a request (« Nouveau ticket ») and follow
// theirs (« Mes tickets »). One form per brand + the default one; texts,
// fields, look, the subject list, display languages and installation. The
// preview is the widget itself (TicketFormPreview mirrors
// ticketStatic/tickets.js), styled by the tenant's settings.
// API: GET/POST/PUT/DELETE /api/tickets/form-settings, GET /api/tickets/embed,
// GET /api/brands/list, GET /api/statistics/overview (30-day tickets of the
// « Ticketing » channel, where form tickets land).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown, ArrowUp, Code, Languages, ListChecks, ListOrdered, MessageSquareText, Palette, Plus, RotateCcw, Sparkles,
  Ticket, Trash2,
} from 'lucide-react';
import TicketFormPreview from './TicketFormPreview';
import {
  AttentionCard, BrandPicker, ColorField, DiscardDialog, EmbedCode, KpiSkeletons, PageSkeleton, PreviewCard,
  RangeField, SaveBar, SwitchRow, authHeaders, channelTraffic, copyText, formatPercent, kitLabels, scrollToId,
  useChannelOverview,
} from './ChannelsKit';
import {
  Badge, Banner, Card, ConfirmDialog, Field, Kpi, OverflowMenu, PageHeader, Toast,
} from '../design/ui';
import './TicketFormSettings.css';

// Must match SUPPORTED_FORM_LANGUAGES in the backend
// (app/services/form_translation_service.py) — the languages the embedded
// widget's own chrome is translated into. Labels are the native names.
export const SUPPORTED_FORM_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
];

export const DEFAULT_SUBJECTS = [
  'Retard de livraison',
  'Demande de localisation',
  'Marchandise manquante',
  'Marchandise endommagée',
  'Colis perdu',
  'Point Relais non respecté',
  'Contestation de livraison',
  'Anomalie douanière',
  'Multi-colis non respecté',
  "Complément d'adresse",
  'Demande de preuve de livraison',
  'Retour non reçu',
  'Demande de retour colis',
  "Anomalie d'enlèvement",
  'Facturation et paiement',
  'Autre',
];

// Backend bounds (TicketFormSettingsRequest): 40 subjects of 120 characters.
const MAX_SUBJECTS = 40;

const clampRadius = (value) => {
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) return 0;
  return Math.min(32, Math.max(0, parsed));
};

// Keep aligned with the backend defaults (_DEFAULTS in
// app/api/ticket_form_settings.py); empty texts mean "use the widget's own
// localized wording".
const DEFAULT_SETTINGS = {
  primary_color: '#1f2937',
  background_color: '#ffffff',
  text_color: '#1f2937',
  accent_color: '#2563eb',
  border_radius: 12,
  title: '',
  intro_text: '',
  success_message: '',
  button_label: '',
  displayed: true,
  show_order_fields: true,
  classify_inbound_subjects: true,
  subjects: [...DEFAULT_SUBJECTS],
  languages: [],
};

const extractSettings = (doc) => ({
  primary_color: doc.primary_color || DEFAULT_SETTINGS.primary_color,
  background_color: doc.background_color || DEFAULT_SETTINGS.background_color,
  text_color: doc.text_color || DEFAULT_SETTINGS.text_color,
  accent_color: doc.accent_color || DEFAULT_SETTINGS.accent_color,
  border_radius: clampRadius(doc.border_radius ?? DEFAULT_SETTINGS.border_radius),
  title: doc.title ?? DEFAULT_SETTINGS.title,
  intro_text: doc.intro_text ?? DEFAULT_SETTINGS.intro_text,
  success_message: doc.success_message ?? DEFAULT_SETTINGS.success_message,
  button_label: doc.button_label ?? DEFAULT_SETTINGS.button_label,
  displayed: doc.displayed ?? true,
  show_order_fields: doc.show_order_fields ?? true,
  classify_inbound_subjects: doc.classify_inbound_subjects ?? true,
  subjects: Array.isArray(doc.subjects) && doc.subjects.length > 0 ? [...doc.subjects] : [...DEFAULT_SUBJECTS],
  languages: Array.isArray(doc.languages) ? [...doc.languages] : [],
});

const freshSettings = () => ({ ...DEFAULT_SETTINGS, subjects: [...DEFAULT_SUBJECTS], languages: [] });

// Selected display languages the saved form has no translation pack for:
// the backend saves without translations when the translation call fails
// (« the admin UI can re-save to retry »).
export const missingTranslations = (doc) => {
  if (!doc || !Array.isArray(doc.languages)) return [];
  const packs = doc.translations || {};
  return doc.languages.filter((code) => !packs[code]);
};

// « À vérifier » of the form being edited.
export const formChecks = (doc, traffic) => {
  const checks = [];
  if (doc && doc.displayed === false) checks.push({ key: 'hidden', tone: 'warning' });
  const missing = missingTranslations(doc);
  if (missing.length) checks.push({ key: 'translations', tone: 'warning', missing });
  if (doc && doc.displayed !== false && traffic && traffic.count === 0) checks.push({ key: 'noTraffic', tone: 'warning' });
  return checks;
};

const languageName = (code) => SUPPORTED_FORM_LANGUAGES.find((l) => l.code === code)?.label || code;

const TicketFormSettings = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const navigate = useNavigate();
  const kit = kitLabels(t);
  const overview = useChannelOverview();

  const [settingsList, setSettingsList] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedBrandKey, setSelectedBrandKey] = useState(''); // '' = brandless default form
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [editSettings, setEditSettings] = useState(freshSettings);
  const [baseline, setBaseline] = useState(freshSettings);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [newSubject, setNewSubject] = useState('');
  const [subjectError, setSubjectError] = useState('');
  const [embed, setEmbed] = useState({ code: '', loading: false, error: '' });
  const [pendingKey, setPendingKey] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const dirty = JSON.stringify(editSettings) !== JSON.stringify(baseline);
  const docForBrandKey = useCallback((key, list = settingsList) => list.find((doc) => (doc.brand_id || '') === key), [settingsList]);
  const currentDoc = docForBrandKey(selectedBrandKey);

  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const applyDoc = (doc) => {
    const settings = doc ? extractSettings(doc) : freshSettings();
    setEditSettings(settings);
    setBaseline(settings);
    setNewSubject('');
    setSubjectError('');
  };

  const fetchFormSettings = useCallback(async () => {
    const response = await axios.get('/api/tickets/form-settings', { headers: authHeaders() });
    const list = response.data || [];
    setSettingsList(list);
    return list;
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('access_token')) {
      navigate('/');
      return;
    }
    (async () => {
      try {
        const userRes = await axios.get('/api/users/user', { headers: authHeaders() });
        const list = await fetchFormSettings();
        if (userRes.data?.has_brand) {
          try {
            const res = await axios.get('/api/brands/list', { headers: authHeaders() });
            setBrands(res.data || []);
          } catch (e) {
            setBrands([]);
          }
        }
        const defaultDoc = list.find((doc) => !doc.brand_id);
        if (defaultDoc) {
          const settings = extractSettings(defaultDoc);
          setEditSettings(settings);
          setBaseline(settings);
        }
      } catch (err) {
        setLoadError(t('ticketFormSettings.loadError', 'Échec du chargement des paramètres du formulaire'));
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, fetchFormSettings, t]);

  // The script tag of the selected brand's form.
  useEffect(() => {
    if (loading) return undefined;
    let alive = true;
    setEmbed({ code: '', loading: true, error: '' });
    const params = selectedBrandKey ? `?brand_id=${selectedBrandKey}` : '';
    axios.get(`/api/tickets/embed${params}`, { headers: authHeaders() })
      .then((res) => { if (alive) setEmbed({ code: res.data?.embed_code || '', loading: false, error: '' }); })
      .catch((err) => {
        if (!alive) return;
        setEmbed({
          code: '',
          loading: false,
          error: err.response?.status === 403
            ? t('channelsUi.form.embedAdminOnly', 'Seul un administrateur du compte peut générer le code d’intégration.')
            : t('ticketFormSettings.embedError', 'Échec de la génération du code d’intégration'),
        });
      });
    return () => { alive = false; };
  }, [selectedBrandKey, loading, t]);

  const set = (patch) => setEditSettings((prev) => ({ ...prev, ...patch }));

  // Tabs: brandless default + one entry per brand (+ orphan docs whose brand
  // no longer exists, kept visible so existing configs never disappear).
  const brandEntries = [
    { key: '', name: t('ticketFormSettings.defaultForm', 'Formulaire par défaut') },
    ...brands.map((brand) => ({ key: brand.id, name: brand.name })),
    ...settingsList
      .filter((doc) => doc.brand_id && !brands.some((b) => b.id === doc.brand_id))
      .map((doc) => ({ key: doc.brand_id, name: t('ticketFormSettings.unknownBrand', 'Marque inconnue') })),
  ];
  const selectedName = brandEntries.find((entry) => entry.key === selectedBrandKey)?.name || '';

  const selectBrand = (key) => {
    setSelectedBrandKey(key);
    applyDoc(docForBrandKey(key));
  };

  const requestBrand = (entry) => {
    if (entry.key === selectedBrandKey) return;
    if (dirty) setPendingKey(entry.key);
    else selectBrand(entry.key);
  };

  // ---------------------------------------------------------------- subjects

  const handleSubjectChange = (index, value) => setEditSettings((prev) => {
    const subjects = [...prev.subjects];
    subjects[index] = value;
    return { ...prev, subjects };
  });

  const handleRemoveSubject = (index) => setEditSettings((prev) => (prev.subjects.length <= 1
    ? prev
    : { ...prev, subjects: prev.subjects.filter((_, i) => i !== index) }));

  const handleMoveSubject = (index, direction) => setEditSettings((prev) => {
    const target = index + direction;
    if (target < 0 || target >= prev.subjects.length) return prev;
    const subjects = [...prev.subjects];
    [subjects[index], subjects[target]] = [subjects[target], subjects[index]];
    return { ...prev, subjects };
  });

  const handleAddSubject = () => {
    const value = newSubject.trim();
    if (!value) return;
    if (editSettings.subjects.some((s) => s.trim().toLowerCase() === value.toLowerCase())) {
      setSubjectError(t('ticketFormSettings.duplicateSubject', 'Ce sujet existe déjà dans la liste'));
      return;
    }
    set({ subjects: [...editSettings.subjects, value] });
    setNewSubject('');
    setSubjectError('');
  };

  const handleToggleLanguage = (code) => setEditSettings((prev) => ({
    ...prev,
    languages: prev.languages.includes(code) ? prev.languages.filter((c) => c !== code) : [...prev.languages, code],
  }));

  const validateSubjects = () => {
    const cleaned = editSettings.subjects.map((s) => s.trim());
    if (cleaned.length === 0 || cleaned.some((s) => !s)) {
      return { error: t('ticketFormSettings.emptySubject', 'Aucun sujet ne peut être vide') };
    }
    const lowered = cleaned.map((s) => s.toLowerCase());
    if (new Set(lowered).size !== lowered.length) {
      return { error: t('ticketFormSettings.duplicateSubject', 'Ce sujet existe déjà dans la liste') };
    }
    return { subjects: cleaned };
  };

  // ---------------------------------------------------------------- save / delete

  const handleSave = async () => {
    const { subjects, error } = validateSubjects();
    if (error) {
      setToast({ tone: 'danger', text: error });
      scrollToId('form-subjects');
      return;
    }
    setSaving(true);
    const payload = {
      primary_color: editSettings.primary_color,
      background_color: editSettings.background_color,
      text_color: editSettings.text_color,
      accent_color: editSettings.accent_color,
      border_radius: clampRadius(editSettings.border_radius),
      title: editSettings.title,
      intro_text: editSettings.intro_text,
      success_message: editSettings.success_message,
      button_label: editSettings.button_label,
      displayed: !!editSettings.displayed,
      show_order_fields: !!editSettings.show_order_fields,
      classify_inbound_subjects: !!editSettings.classify_inbound_subjects,
      subjects,
      languages: editSettings.languages,
    };
    try {
      if (currentDoc) {
        await axios.put(`/api/tickets/form-settings/${currentDoc.id}`, payload, { headers: authHeaders() });
        setToast({ tone: 'success', text: t('ticketFormSettings.updateSuccess', 'Paramètres enregistrés avec succès') });
      } else {
        await axios.post('/api/tickets/form-settings', { ...payload, brand_id: selectedBrandKey || null }, { headers: authHeaders() });
        setToast({ tone: 'success', text: t('ticketFormSettings.createSuccess', 'Formulaire créé avec succès') });
      }
      const list = await fetchFormSettings();
      applyDoc(docForBrandKey(selectedBrandKey, list));
    } catch (err) {
      if (err.response?.status === 409) {
        setToast({ tone: 'danger', text: t('ticketFormSettings.alreadyExists', 'Un formulaire existe déjà pour cette marque. Rechargement...') });
        await fetchFormSettings();
      } else {
        setToast({ tone: 'danger', text: err.response?.data?.detail || t('ticketFormSettings.saveError', 'Échec de l’enregistrement des paramètres') });
      }
    } finally {
      setSaving(false);
    }
  };

  const runDelete = async () => {
    if (!currentDoc) return;
    setDeleteBusy(true);
    try {
      await axios.delete(`/api/tickets/form-settings/${currentDoc.id}`, { headers: authHeaders() });
      const list = await fetchFormSettings();
      applyDoc(docForBrandKey(selectedBrandKey, list));
      setToast({ tone: 'success', text: t('ticketFormSettings.deleteSuccess', 'Formulaire supprimé avec succès') });
    } catch (err) {
      setToast({ tone: 'danger', text: t('ticketFormSettings.deleteError', 'Échec de la suppression du formulaire') });
    } finally {
      setDeleteBusy(false);
      setDeleting(false);
    }
  };

  const copyEmbed = async () => {
    const ok = await copyText(embed.code);
    setToast(ok ? { tone: 'success', text: kit.embed.copiedToast } : { tone: 'danger', text: kit.embed.copyFailed });
  };

  // ---------------------------------------------------------------- derived

  const traffic = useMemo(() => channelTraffic(overview.overview, 'Ticketing'), [overview.overview]);
  const missing = missingTranslations(currentDoc);
  const checks = formChecks(currentDoc, traffic);
  const checkText = (check) => {
    switch (check.key) {
    case 'hidden':
      return {
        title: t('channelsUi.form.checkHidden', 'Le formulaire est masqué : vos clients ne peuvent plus ouvrir de demande'),
        detail: t('channelsUi.form.checkHiddenDetail', 'Le code reste installé ; réactivez l’affichage dans « Installation ».'),
        action: { label: t('channelsUi.openInstall', 'Voir l’installation'), onClick: () => scrollToId('form-install') },
      };
    case 'translations':
      return {
        title: t('channelsUi.form.checkTranslations', 'Traductions manquantes : {{languages}}', { languages: check.missing.map(languageName).join(', ') }),
        detail: t('channelsUi.form.checkTranslationsDetail', 'Les visiteurs de ces langues voient vos textes d’origine. Enregistrez à nouveau pour relancer la traduction.'),
        action: { label: t('channelsUi.form.retranslate', 'Relancer la traduction'), onClick: handleSave, disabled: saving || dirty },
      };
    default:
      return {
        title: t('channelsUi.form.checkNoTraffic', 'Aucun ticket reçu par le formulaire depuis 30 jours'),
        detail: t('channelsUi.form.checkNoTrafficDetail', 'Vérifiez que le code d’intégration est bien collé sur votre site.'),
        action: { label: t('channelsUi.openCode', 'Voir le code'), onClick: () => scrollToId('form-install') },
      };
    }
  };

  const configuredCount = brandEntries.filter((entry) => docForBrandKey(entry.key)).length;
  const visibleCount = brandEntries.filter((entry) => (docForBrandKey(entry.key)?.displayed ?? false)).length;
  const pickerEntries = brandEntries.map((entry) => {
    const doc = docForBrandKey(entry.key);
    return {
      ...entry,
      color: doc?.primary_color,
      meta: doc
        ? [
          t('channelsUi.form.subjectsCount', '{{count}} sujets', { count: (doc.subjects || []).length }),
          (doc.languages || []).length ? (doc.languages || []).map((code) => code.toUpperCase()).join(' · ') : null,
        ].filter(Boolean).join(' · ')
        : t('channelsUi.form.usesDefault', 'Pas encore de formulaire propre'),
      badge: !doc
        ? <Badge>{kit.notConfigured}</Badge>
        : (doc.displayed ?? true) ? <Badge tone="success" dot>{kit.visible}</Badge> : <Badge>{kit.hidden}</Badge>,
    };
  });

  const header = (
    <PageHeader
      title={t('ticketFormSettings.title', 'Formulaire de tickets')}
      subtitle={brandEntries.length > 1
        ? t('channelsUi.form.subtitleCount', '{{count}} formulaires configurés · {{visible}} visibles sur vos sites', { count: configuredCount, visible: visibleCount })
        : t('channelsUi.form.subtitle', 'Le formulaire de votre site : vos clients ouvrent une demande et suivent leurs tickets dans « Mes tickets ».')}
      actions={(
        <>
          {currentDoc && (
            <OverflowMenu
              label={kit.moreActions}
              items={[{ label: t('channelsUi.form.delete', 'Supprimer ce formulaire'), icon: Trash2, danger: true, onClick: () => setDeleting(true) }]}
            />
          )}
          <button type="button" className="u-btn u-btn--secondary" onClick={copyEmbed} disabled={!embed.code}>
            <Code aria-hidden="true" />
            {kit.embed.copyCode}
          </button>
        </>
      )}
    />
  );

  if (loading) {
    return (
      <div className="u-page u-page--narrow chan-v2" aria-busy="true" aria-label={t('ticketFormSettings.loading', 'Chargement des paramètres du formulaire...')}>
        <PageSkeleton header={header} />
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="u-page u-page--narrow chan-v2">
        {header}
        <div className="u-well"><Banner tone="danger">{loadError}</Banner></div>
      </div>
    );
  }

  const subjects = editSettings.subjects;
  const savedDisplayed = currentDoc ? (currentDoc.displayed ?? true) : null;

  return (
    <div className="u-page u-page--narrow chan-v2">
      {header}

      <div className="u-well">
        {brandEntries.length > 1 && (
          <BrandPicker label={kit.brands} entries={pickerEntries} selectedKey={selectedBrandKey} onSelect={requestBrand} />
        )}

        <AttentionCard
          items={checks.map((check) => ({ ...check, ...checkText(check) }))}
          title={kit.attention}
          criticalLabel={kit.critical}
          watchLabel={kit.watch}
        />

        {overview.status === 'loading' && <KpiSkeletons />}
        {traffic && (
          <div className="u-kpi-row">
            <Kpi
              icon={Ticket}
              label={t('channelsUi.form.kpiTickets', 'Tickets du formulaire')}
              value={traffic.count.toLocaleString(lang)}
              context={t('channelsUi.kpiTicketsContext', '30 derniers jours · {{share}} des tickets du compte', { share: formatPercent(traffic.share, lang) })}
            />
            {traffic.count > 0 && (
              <Kpi
                icon={Sparkles}
                tone="success"
                label={t('channelsUi.kpiAi', 'Résolus par l’IA')}
                value={formatPercent(traffic.aiRate, lang)}
                meter={{ value: traffic.aiRate, max: 100, tone: 'success', label: t('channelsUi.kpiAi', 'Résolus par l’IA') }}
                context={t('channelsUi.kpiAiContext', '{{resolved}} tickets sur {{total}} · 30 jours', { resolved: traffic.aiResolved.toLocaleString(lang), total: traffic.count.toLocaleString(lang) })}
              />
            )}
            {currentDoc && (
              <Kpi
                icon={ListOrdered}
                tone={savedDisplayed ? 'info' : 'neutral'}
                label={t('channelsUi.form.kpiSubjects', 'Sujets proposés')}
                value={(currentDoc.subjects || []).length}
                context={[
                  savedDisplayed ? kit.visible : kit.hidden,
                  (currentDoc.languages || []).length
                    ? t('channelsUi.form.kpiLanguages', 'traduit en {{languages}}', { languages: currentDoc.languages.map(languageName).join(', ') })
                    : t('channelsUi.form.kpiOneLanguage', 'une seule langue'),
                ].join(' · ')}
              />
            )}
          </div>
        )}

        <div className="chan-v2__layout">
          <div className="chan-v2__column">
            {!currentDoc && (
              <Banner
                tone="info"
                title={selectedBrandKey
                  ? t('channelsUi.form.noDocBrandTitle', '« {{name}} » n’a pas encore son formulaire', { name: selectedName })
                  : t('channelsUi.form.noDocTitle', 'Pas encore de formulaire par défaut')}
              >
                {selectedBrandKey
                  ? t('channelsUi.form.noDocBrand', 'Ses visiteurs voient le formulaire par défaut. Réglez celui-ci puis créez-le pour qu’il remplace le formulaire par défaut sur les sites de la marque.')
                  : t('channelsUi.form.noDoc', 'Réglez le formulaire puis créez-le : il s’affichera là où son code est installé.')}
              </Banner>
            )}

            <Card
              id="form-texts"
              icon={MessageSquareText}
              title={t('ticketFormSettings.sectionTexts', 'Textes')}
              description={t('channelsUi.form.textsDesc', 'Laissés vides, les textes du widget s’affichent, traduits dans la langue du visiteur.')}
            >
              <div className="u-form">
                <div className="u-form-grid">
                  <Field label={t('ticketFormSettings.formTitle', 'Titre du formulaire')} htmlFor="tfs-title-input">
                    <input
                      id="tfs-title-input"
                      className="u-input"
                      value={editSettings.title}
                      maxLength={120}
                      placeholder={t('ticketFormSettings.formTitlePlaceholder', 'Ex. : Créer un nouveau ticket')}
                      onChange={(e) => set({ title: e.target.value })}
                    />
                  </Field>
                  <Field label={t('ticketFormSettings.buttonLabel', 'Libellé du bouton d’envoi')} htmlFor="tfs-button-input">
                    <input
                      id="tfs-button-input"
                      className="u-input"
                      value={editSettings.button_label}
                      maxLength={60}
                      placeholder={t('ticketFormSettings.buttonLabelPlaceholder', 'Soumettre le Ticket')}
                      onChange={(e) => set({ button_label: e.target.value })}
                    />
                  </Field>
                </div>
                <Field label={t('ticketFormSettings.introText', 'Texte d’introduction')} htmlFor="tfs-intro-input">
                  <textarea id="tfs-intro-input" className="u-textarea" rows={3} maxLength={600} value={editSettings.intro_text} onChange={(e) => set({ intro_text: e.target.value })} />
                </Field>
                <Field
                  label={t('ticketFormSettings.successMessage', 'Message de confirmation')}
                  htmlFor="tfs-success-input"
                  help={t('channelsUi.form.successHelp', 'Affiché au client une fois sa demande envoyée.')}
                >
                  <textarea id="tfs-success-input" className="u-textarea" rows={3} maxLength={600} value={editSettings.success_message} onChange={(e) => set({ success_message: e.target.value })} />
                </Field>
              </div>
            </Card>

            <Card
              id="form-fields"
              icon={ListChecks}
              title={t('channelsUi.form.fieldsTitle', 'Champs et classement')}
            >
              <div className="chan-v2__switches">
                <SwitchRow
                  id="tfs-show-order-fields"
                  label={t('ticketFormSettings.showOrderFields', 'Afficher les champs commande / suivi')}
                  help={t('channelsUi.form.orderFieldsHelp', 'Numéros de suivi, de commande et de facture, facultatifs : l’agent IA retrouve le colis sans redemander.')}
                  checked={editSettings.show_order_fields}
                  onChange={(value) => set({ show_order_fields: value })}
                />
                <SwitchRow
                  id="tfs-classify-subjects"
                  label={t('ticketFormSettings.classifyInboundSubjects', 'Classer les sujets entrants par IA (e-mails, réseaux sociaux, chatbot)')}
                  help={t('channelsUi.form.classifyHelp', 'Les demandes des autres canaux reçoivent un sujet de cette liste : vos statistiques par motif restent comparables.')}
                  checked={editSettings.classify_inbound_subjects}
                  onChange={(value) => set({ classify_inbound_subjects: value })}
                />
              </div>
            </Card>

            <Card
              id="form-design"
              icon={Palette}
              title={t('ticketFormSettings.sectionDesign', 'Apparence')}
              description={t('channelsUi.form.designDesc', 'Les couleurs et l’arrondi du formulaire sur votre site.')}
            >
              <div className="u-form">
                <div className="u-form-grid">
                  <ColorField id="tfs-primary_color" label={t('ticketFormSettings.primaryColor', 'Couleur principale')} value={editSettings.primary_color} help={t('channelsUi.form.primaryHelp', 'Bouton d’envoi.')} onChange={(v) => set({ primary_color: v })} />
                  <ColorField id="tfs-accent_color" label={t('ticketFormSettings.accentColor', 'Couleur d’accent')} value={editSettings.accent_color} help={t('channelsUi.form.accentHelp', 'Onglet actif, focus des champs.')} onChange={(v) => set({ accent_color: v })} />
                  <ColorField id="tfs-background_color" label={t('ticketFormSettings.backgroundColor', 'Couleur de fond')} value={editSettings.background_color} onChange={(v) => set({ background_color: v })} />
                  <ColorField id="tfs-text_color" label={t('ticketFormSettings.textColor', 'Couleur du texte')} value={editSettings.text_color} onChange={(v) => set({ text_color: v })} />
                </div>
                <RangeField
                  id="tfs-border-radius"
                  label={t('ticketFormSettings.borderRadius', 'Arrondi des angles')}
                  value={editSettings.border_radius}
                  unit="px"
                  min={0}
                  max={32}
                  withNumber
                  onChange={(v) => set({ border_radius: clampRadius(v) })}
                />
              </div>
            </Card>

            <Card
              id="form-subjects"
              icon={ListOrdered}
              title={t('ticketFormSettings.sectionSubjects', 'Sujets')}
              badge={<Badge tone="accent">{subjects.length}</Badge>}
              description={t('channelsUi.form.subjectsDesc', 'Les motifs du menu « Sujet ». Le motif choisi devient le titre du ticket ; l’ordre ici est celui du menu.')}
              actions={(
                <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => set({ subjects: [...DEFAULT_SUBJECTS] })}>
                  <RotateCcw aria-hidden="true" />
                  {t('ticketFormSettings.resetSubjects', 'Réinitialiser la liste par défaut')}
                </button>
              )}
              flush
            >
              <ol className="u-list tform-v2__subjects">
                {subjects.map((subject, index) => (
                  <li className="u-row tform-v2__subject" key={index}>
                    <span className="tform-v2__subject-index u-num" aria-hidden="true">{index + 1}</span>
                    <input
                      className="u-input"
                      value={subject}
                      maxLength={120}
                      aria-label={t('channelsUi.form.subjectLabel', 'Sujet {{n}}', { n: index + 1 })}
                      aria-invalid={!subject.trim()}
                      onChange={(e) => handleSubjectChange(index, e.target.value)}
                    />
                    <div className="u-row__side tform-v2__subject-actions">
                      <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={() => handleMoveSubject(index, -1)} disabled={index === 0}
                        title={t('ticketFormSettings.moveUp', 'Monter')} aria-label={t('ticketFormSettings.moveUp', 'Monter')}>
                        <ArrowUp aria-hidden="true" />
                      </button>
                      <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={() => handleMoveSubject(index, 1)} disabled={index === subjects.length - 1}
                        title={t('ticketFormSettings.moveDown', 'Descendre')} aria-label={t('ticketFormSettings.moveDown', 'Descendre')}>
                        <ArrowDown aria-hidden="true" />
                      </button>
                      <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm tform-v2__remove" onClick={() => handleRemoveSubject(index)} disabled={subjects.length <= 1}
                        title={t('ticketFormSettings.removeSubject', 'Supprimer le sujet')} aria-label={t('ticketFormSettings.removeSubject', 'Supprimer le sujet')}>
                        <Trash2 aria-hidden="true" />
                      </button>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="tform-v2__subject-add">
                <div className="u-inline-form">
                  <input
                    className="u-input"
                    value={newSubject}
                    maxLength={120}
                    disabled={subjects.length >= MAX_SUBJECTS}
                    aria-label={t('ticketFormSettings.addSubjectPlaceholder', 'Nouveau sujet...')}
                    placeholder={t('ticketFormSettings.addSubjectPlaceholder', 'Nouveau sujet...')}
                    onChange={(e) => { setNewSubject(e.target.value); setSubjectError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSubject(); } }}
                  />
                  <button type="button" className="u-btn u-btn--secondary" onClick={handleAddSubject} disabled={!newSubject.trim() || subjects.length >= MAX_SUBJECTS}>
                    <Plus aria-hidden="true" />
                    {t('ticketFormSettings.addSubject', 'Ajouter')}
                  </button>
                </div>
                {subjectError
                  ? <p className="u-error-text" role="alert">{subjectError}</p>
                  : <p className="u-help">{t('channelsUi.form.subjectsLimit', '{{count}} sujets sur {{max}} possibles, 120 caractères chacun.', { count: subjects.length, max: MAX_SUBJECTS })}</p>}
              </div>
            </Card>

            <Card
              id="form-languages"
              icon={Languages}
              title={t('ticketFormSettings.sectionLanguages', 'Langues')}
              badge={editSettings.languages.length ? <Badge tone="accent">{editSettings.languages.length}</Badge> : null}
              description={t('ticketFormSettings.languagesHint', 'Activez des langues d’affichage supplémentaires : les sujets et textes sont traduits automatiquement à l’enregistrement et affichés selon la langue du navigateur du visiteur, avec l’anglais par défaut. Les tickets conservent le sujet d’origine.')}
            >
              <div className="u-form">
                <div className="tform-v2__languages">
                  {SUPPORTED_FORM_LANGUAGES.map(({ code, label }) => {
                    const on = editSettings.languages.includes(code);
                    return (
                      <label key={code} className={`tform-v2__language${on ? ' is-on' : ''}`}>
                        <input type="checkbox" checked={on} onChange={() => handleToggleLanguage(code)} />
                        <span>{label}</span>
                        <span className="u-code">{code.toUpperCase()}</span>
                      </label>
                    );
                  })}
                </div>
                {editSettings.languages.length > 0 && (
                  currentDoc && Object.keys(currentDoc.translations || {}).length > 0 && !dirty ? (
                    <p className="u-help">
                      {currentDoc.source_language && (
                        <>
                          {t('ticketFormSettings.detectedLanguage', 'Langue détectée du contenu')}
                          {' : '}
                          <strong>{currentDoc.source_language}</strong>
                          {' · '}
                        </>
                      )}
                      {t('ticketFormSettings.translationsReady', 'Traductions disponibles')}
                      {' : '}
                      <strong>{Object.keys(currentDoc.translations).join(', ')}</strong>
                      {missing.length > 0 && ` · ${t('channelsUi.form.translationsMissing', 'manquantes : {{languages}}', { languages: missing.join(', ') })}`}
                    </p>
                  ) : (
                    <p className="u-help">{t('ticketFormSettings.translationsPending', 'Les traductions seront générées à l’enregistrement.')}</p>
                  )
                )}
              </div>
            </Card>

            <Card
              id="form-install"
              icon={Code}
              title={t('channelsUi.install', 'Installation')}
              description={t('ticketFormSettings.embedHint', 'Collez ce code dans le HTML de votre site pour afficher le formulaire de tickets.')}
            >
              <div className="u-form">
                <div className="chan-v2__switches">
                  <SwitchRow
                    id="tfs-displayed"
                    label={t('ticketFormSettings.displayedLabel', 'Afficher le formulaire')}
                    help={t('channelsUi.form.displayedHelp', 'Désactivé : le formulaire disparaît de votre site sans retirer le code.')}
                    checked={editSettings.displayed}
                    onChange={(value) => set({ displayed: value })}
                  />
                </div>
                <div className="u-field">
                  <span className="u-label">{t('ticketFormSettings.embedTitle', 'Code d’intégration')}</span>
                  <EmbedCode
                    code={embed.code}
                    loading={embed.loading}
                    error={embed.error}
                    copyLabel={kit.embed.copy}
                    copiedLabel={kit.embed.copied}
                    loadingLabel={kit.embed.loading}
                  />
                </div>
              </div>
            </Card>

            <SaveBar
              dirty={dirty}
              saving={saving}
              canSave={dirty || !currentDoc}
              onSave={handleSave}
              onReset={() => { setEditSettings(baseline); setSubjectError(''); }}
              note={brandEntries.length > 1 ? selectedName : null}
              labels={{
                ...kit.save,
                save: currentDoc ? kit.save.save : t('channelsUi.form.create', 'Créer le formulaire'),
              }}
            />
          </div>

          <PreviewCard
            title={kit.preview}
            badge={!editSettings.displayed ? <Badge>{kit.hiddenOnSite}</Badge> : null}
            note={t('channelsUi.form.previewNote', 'Le formulaire tel que vos clients le voient ; l’onglet « Mes tickets » montre leur suivi.')}
          >
            <div className="tfp-stage">
              <TicketFormPreview settings={editSettings} />
            </div>
          </PreviewCard>
        </div>
      </div>

      {pendingKey !== null && (
        <DiscardDialog
          t={t}
          name={selectedName}
          onConfirm={() => { selectBrand(pendingKey); setPendingKey(null); }}
          onCancel={() => setPendingKey(null)}
        />
      )}

      {deleting && currentDoc && (
        <ConfirmDialog
          title={t('channelsUi.form.deleteTitle', 'Supprimer le formulaire « {{name}} » ?', { name: selectedName })}
          message={selectedBrandKey
            ? t('channelsUi.form.deleteBrandMessage', 'Les sites de la marque afficheront le formulaire par défaut. Les tickets déjà reçus sont conservés.')
            : t('channelsUi.form.deleteDefaultMessage', 'Les sites où son code est installé afficheront le formulaire avec l’apparence et les sujets par défaut. Les tickets déjà reçus sont conservés.')}
          confirmLabel={t('ticketFormSettings.delete', 'Supprimer')}
          cancelLabel={kit.cancel}
          icon={Trash2}
          busy={deleteBusy}
          onConfirm={runDelete}
          onCancel={() => setDeleting(false)}
        />
      )}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={kit.close}>{toast.text}</Toast>
      )}
    </div>
  );
};

export default TicketFormSettings;
